//#region IMPORTS
import * as XLSX from "xlsx";
import { InstoreOrder } from "../models/instoreOrder";
import { ORDERSTATUS } from "../models/order";
//#endregion

// Track & trace import for instore orders (customer request, Sept 2026):
// an Excel / CSV with the order code (e.g. WEEK__6022__202609141917), the
// full track & trace link and an optional ACTION, one row per parcel. Pure
// helpers — parsing, the template and the check step; the wizard is
// InstoreOrdersTrackAndTraceImport.
//
// An instore order stores its parcels as ONE string, links joined by ";".
// A row is one order code + one link. Several parcels = several rows.
//
// ACTION (optional column, case-insensitive):
//   ADD    (default, also when empty or when there is no ACTION column)
//          → add the link; already on the order = skipped;
//   DELETE → remove that link; not on the order = skipped.
// There is deliberately no UPDATE: with one link column it is ambiguous. To
// replace a link use two rows, DELETE the old one and ADD the new one.

export type TrackAndTraceAction = "ADD" | "DELETE";

export interface ParsedTrackAndTraceRow {
  rowNumber: number; // as in Excel (1-based, header = 1)
  orderCode: string;
  link: string;
  action: string; // raw, trimmed + upper-cased; "" = not given
}

export type TrackAndTraceRowStatus =
  | "new"
  | "remove"
  | "present"
  | "notOnOrder"
  | "duplicate"
  | "notFound"
  | "skippedStatus"
  | "invalidLink"
  | "missingCode"
  | "unsupportedAction"
  | "unknownAction"
  | "lookupFailed";

export interface CheckedTrackAndTraceRow extends ParsedTrackAndTraceRow {
  status: TrackAndTraceRowStatus;
  // Normalised action (empty → ADD); undefined when the action is not valid.
  resolvedAction?: TrackAndTraceAction;
  order?: InstoreOrder;
}

export const TRACK_AND_TRACE_STATUS_LABEL: Record<
  TrackAndTraceRowStatus,
  string
> = {
  new: "will be added",
  remove: "will be removed",
  present: "already on the order",
  notOnOrder: "not on the order",
  duplicate: "duplicate in file",
  notFound: "order not found",
  skippedStatus: "order cancelled / archived",
  invalidLink: "not a valid link",
  missingCode: "no order code",
  unsupportedAction: "UPDATE not supported, use DELETE + ADD",
  unknownAction: "unknown action",
  lookupFailed: "could not be checked",
};

export const TEMPLATE_HEADERS = ["ORDER_CODE", "TRACK_AND_TRACE", "ACTION"];

// The ";"-joined parcels of an order as a clean list.
export function splitTrackAndTrace(value?: string | null): string[] {
  return (value ?? "")
    .split(";")
    .map((link) => link.trim())
    .filter(Boolean);
}

// ONE link per row. A valid link has no whitespace and no ";" — a cell with
// two links (or the app's own ";" separator) is rejected as "not a valid
// link" instead of being stored as one broken entry.
const isLink = (value: string) => /^https?:\/\/[^\s;]+$/i.test(value);

// Reads the first sheet. Columns are found by their header — something with
// "order" for the code, "track" / "trace" / "link" / "url" for the link, and
// "action" / "actie" for the optional action. Without a recognisable header,
// but with an order code in the first cell, columns A (code), B (link) and C
// (action) are used and row 1 is data. A hyperlink cell counts with its
// target URL, not its display text.
export async function parseTrackAndTraceFile(
  file: File,
): Promise<ParsedTrackAndTraceRow[]> {
  const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet || !sheet["!ref"]) throw new Error("The file is empty.");
  const range = XLSX.utils.decode_range(sheet["!ref"]);

  const cellText = (r: number, c: number | null): string => {
    if (c === null) return "";
    const cell = sheet[XLSX.utils.encode_cell({ r, c })];
    if (!cell) return "";
    const value = cell.l?.Target ?? cell.w ?? cell.v;
    return value === undefined || value === null ? "" : String(value).trim();
  };

  const headers: string[] = [];
  for (let c = range.s.c; c <= range.e.c; c++)
    headers.push(cellText(range.s.r, c).toLowerCase());
  const linkIndex = headers.findIndex((h) => /track|trace|link|url/.test(h));
  const actionIndex = headers.findIndex(
    (h, i) => i !== linkIndex && /action|actie/.test(h),
  );
  const orderIndex = headers.findIndex(
    (h, i) => i !== linkIndex && i !== actionIndex && /order/.test(h),
  );

  let orderCol: number;
  let linkCol: number;
  let actionCol: number | null;
  let firstDataRow: number;
  if (orderIndex >= 0 && linkIndex >= 0) {
    orderCol = range.s.c + orderIndex;
    linkCol = range.s.c + linkIndex;
    actionCol = actionIndex >= 0 ? range.s.c + actionIndex : null;
    firstDataRow = range.s.r + 1;
  } else if (cellText(range.s.r, range.s.c).includes("__")) {
    orderCol = range.s.c;
    linkCol = range.s.c + 1;
    actionCol = range.s.c + 2 <= range.e.c ? range.s.c + 2 : null;
    firstDataRow = range.s.r;
  } else {
    throw new Error(
      "Could not find an order code column and a track & trace column. Use the template.",
    );
  }

  const rows: ParsedTrackAndTraceRow[] = [];
  for (let r = firstDataRow; r <= range.e.r; r++) {
    const orderCode = cellText(r, orderCol);
    const link = cellText(r, linkCol);
    if (!orderCode && !link) continue;
    rows.push({
      rowNumber: r + 1,
      orderCode,
      link,
      action: cellText(r, actionCol).toUpperCase(),
    });
  }
  return rows;
}

export function downloadTrackAndTraceTemplate(): void {
  const worksheet = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS]);
  worksheet["!cols"] = [{ wch: 30 }, { wch: 60 }, { wch: 10 }];
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "track and trace");
  XLSX.writeFile(workbook, "track_and_trace_import_template.xlsx");
}

// The check step. `orders` maps each order code to its order, null when it
// does not exist, or undefined when the lookup failed. Rows are checked in
// file order, so the first of two identical rows is the one that counts.
// Compared against the order's links as they were at check time; saving
// applies the same changes to a fresh copy of the order.
export function checkTrackAndTraceRows(
  rows: ParsedTrackAndTraceRow[],
  orders: Map<string, InstoreOrder | null | undefined>,
): CheckedTrackAndTraceRow[] {
  const seen = new Set<string>();
  return rows.map((row): CheckedTrackAndTraceRow => {
    if (!row.orderCode) return { ...row, status: "missingCode" };
    if (row.action === "UPDATE") return { ...row, status: "unsupportedAction" };
    if (row.action !== "" && row.action !== "ADD" && row.action !== "DELETE")
      return { ...row, status: "unknownAction" };
    const resolvedAction: TrackAndTraceAction =
      row.action === "DELETE" ? "DELETE" : "ADD";
    if (!isLink(row.link))
      return { ...row, resolvedAction, status: "invalidLink" };

    const order = orders.get(row.orderCode);
    if (order === undefined)
      return { ...row, resolvedAction, status: "lookupFailed" };
    if (order === null) return { ...row, resolvedAction, status: "notFound" };
    if (
      order.order_status_name === ORDERSTATUS.cancelled ||
      order.order_status_name === ORDERSTATUS.archived
    )
      return { ...row, resolvedAction, status: "skippedStatus", order };

    const onOrder = splitTrackAndTrace(order.track_and_trace).includes(
      row.link,
    );
    if (resolvedAction === "ADD" && onOrder)
      return { ...row, resolvedAction, status: "present", order };
    if (resolvedAction === "DELETE" && !onOrder)
      return { ...row, resolvedAction, status: "notOnOrder", order };

    const key = `${row.orderCode}\u0000${resolvedAction}\u0000${row.link}`;
    if (seen.has(key))
      return { ...row, resolvedAction, status: "duplicate", order };
    seen.add(key);
    return {
      ...row,
      resolvedAction,
      status: resolvedAction === "DELETE" ? "remove" : "new",
      order,
    };
  });
}
